import { Link } from "expo-router";
import { Button, Card, Scrollbar } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { ScrollView, View, YStack } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { api } from "~/utils/api";
import { dateToDDMMYY } from "~/utils/date-to-readable";
import { CompleteResearchDialog } from "./complete-research-dialog";
import { ResearchStep } from "./research-step";

type Research = RouterOutputs["research"]["list"]["data"][number];

export const ResearchSteps: React.FC<Research> = ({
  type,
  id,
  name,
  createdAt,
  isActive,
  usersCount,
}) => {
  const { t } = useTranslation("research");
  const [isFirstSliceFinnished] =
    api.research.reseachHasFinnishedFirstSlice.useSuspenseQuery({
      researchId: id,
    });

  const [hasSlices] = api.research.reseachHasSlices.useSuspenseQuery({
    researchId: id,
  });

  const color = isActive ? "$gray-950" : "$gray-secondary-400";

  return (
    <Card
      data-id={`step-card`}
      paddingHorizontal={"$6"}
      gap={"$7"}
      flexDirection="row"
      shadow="md"
      minHeight={404}
    >
      <View maxWidth={320} justifyContent={"space-between"}>
        <View gap={"$1"}>
          <Card.Title
            data-id={`step-card-title`}
            textAlign={"left"}
            color={color}
          >
            {name}
          </Card.Title>
          <YStack gap={"$0.5"}>
            <Card.Subtitle data-id={`step-card-participants`} color={color}>
              {t("research-list-page.research-card.stats.participants-count", {
                count: usersCount,
              })}
            </Card.Subtitle>
            <Card.Subtitle data-id={`step-card-createdAt`} color={color}>
              {t("research-list-page.research-card.stats.createdAt", {
                date: dateToDDMMYY(createdAt),
              })}
            </Card.Subtitle>
          </YStack>
        </View>
        <View gap={"$1"} maxWidth={240}>
          <Link href={`/research/${type}/${id}/results`} asChild>
            <Button
              data-id={`step-card-button-show-results`}
              variant="primary"
              size="md"
              rounded="lg"
              width={"100%"}
            >
              {t("research-list-page.research-card.action.results")}
            </Button>
          </Link>

          {!isFirstSliceFinnished ? (
            <Link href={`/research/${type}/${id}/participants`} asChild>
              <Button
                data-id={`step-card-button-edit-participants`}
                variant="outlined"
                size="md"
                rounded="lg"
                width={"100%"}
              >
                {t("research-list-page.research-card.action.edit-participants")}
              </Button>
            </Link>
          ) : null}

          {hasSlices ? (
            isActive ? (
              <CompleteResearchDialog
                researchId={id}
                trigger={
                  <Button
                    data-id={`step-card-button-finish-research`}
                    variant="outlined"
                    size="md"
                    rounded="lg"
                    width={"100%"}
                  >
                    {t(
                      "research-list-page.research-card.action.finish-research",
                    )}
                  </Button>
                }
              />
            ) : (
              <Button
                data-id={`step-card-button-finished-research`}
                disabled
                variant="outlined"
                size="md"
                rounded="lg"
                width={"100%"}
              >
                {t("research-list-page.research-card.action.finished-research")}
              </Button>
            )
          ) : null}
        </View>
      </View>

      <Scrollbar.Wrapper gap={"$2"} flex={7} horizontal>
        <ScrollView
          scrollEventThrottle={12}
          contentContainerStyle={{ flexDirection: "row", gap: 16 }}
        >
          <ResearchStep
            isActiveResearch={isActive}
            totalUsers={usersCount}
            researchId={id}
          />
        </ScrollView>
        <Scrollbar.Track
          justifyContent="center"
          backgroundColor={"$gray-200"}
          thickness={2}
        >
          <Scrollbar.Thumb
            backgroundColor={"$green-500"}
            borderRadius={"$5"}
            thickness={4}
          />
        </Scrollbar.Track>
      </Scrollbar.Wrapper>
    </Card>
  );
};
